// ---------- backend API ----------
// The backend (Cloudflare Worker, cf. backend/) serves the catalog of puzzles: ones
// curated in administration, and ones submitted by players (held as drafts until an
// admin reviews and publishes them). URL configurable at build time (VITE_API_URL);
// the local Worker as a default in dev covers the common case with nothing to configure.

import { knownAlphabetIds } from '../shared/alphabets';
import { CATEGORY_LABELS } from './category';
import { currentLang } from './i18n';

const API_URL = (
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787'
).replace(/\/$/, '');

export interface PuzzleSummary {
  id: string;
  title: string;
  alphabetId: string;
  gridW: number;
  gridH: number;
  /** Le nombre de gestes que la feuille demande — ses zones 4-connexes, cf. shared/effort.ts.
   *  OPTIONNEL, et le rester : une feuille publiée avant la colonne n'en a pas, et le catalogue
   *  retombe alors sur ses dimensions plutôt que d'estimer sur elles (quatre fois plus faux). */
  zones?: number | null;
  category: string | null;
  hasCover: boolean;
  createdAt: string;
  /** À la une : en tête de la bibliothèque (cf. orderPuzzles dans home.ts). Absent d'un serveur
   *  ou d'un cache d'avant ce champ. */
  featured?: boolean;
  /** Le pack de la feuille, nul hors pack (feuilles gratuites). Absent d'un serveur ou d'un cache
   *  d'avant le rangement par pack (2026-10-23) : la feuille se range alors avec les gratuites. */
  packId?: string | null;
  /** Le nom de ce pack dans la langue du joueur (le thème traduit, à défaut le titre) et son
   *  numéro quand plusieurs packs partagent le thème — les mêmes que dans la vitrine. Envoyés
   *  seulement si la requête déclare `lang`, ce que fait fetchPuzzles(). */
  packName?: string;
  packVolume?: number | null;
  /** La feuille vient du pack de démarrage, offert à tous : elle se range avec les gratuites,
   *  sous « Pour commencer », et n'a donc pas de `packName`. */
  packStarter?: boolean;
}

export interface PuzzleDetail {
  id: string;
  title: string;
  alphabetId: string;
  gridW: number;
  gridH: number;
  target: number[][];
}

export function puzzleCoverUrl(id: string): string {
  return `${API_URL}/puzzles/${id}/cover`;
}

/**
 * La grille seule, PAR LA ROUTE QUI NE FILTRE PAS PAR PACK.
 *
 * `GET /puzzles/:id` garde son garde de pack : une feuille venue d'un pack y répond 404 à qui
 * ne l'a pas débloquée. `GET /puzzles/:id/sheet` en est exempté par construction (même exemption
 * que la page de partage, cf. la note au-dessus de la route côté backend), et c'est exactement
 * ce qu'il faut ici : on reconstruit la vignette d'une feuille DÉJÀ TERMINÉE par le joueur, sur
 * un appareil qui vient d'être réinstallé et où le déblocage du pack n'est pas forcément revenu.
 * Passer par l'autre route rendrait la réparation impossible précisément pour les puzzles qui
 * ont coûté le plus au joueur.
 *
 * Sans jeton, pour la même raison : rien de ce qu'elle rend n'est privé, et une reconstruction
 * ne doit pas dépendre d'une session rétablie.
 */
export async function fetchSheet(id: string): Promise<PuzzleDetail> {
  const res = await fetch(`${API_URL}/puzzles/${encodeURIComponent(id)}/sheet`);
  if (!res.ok) throw new HttpError(res.status);
  return res.json();
}

/** `Authorization` only when a token is actually held — GET /puzzles and GET /puzzles/:id stay
 *  fully usable with none (an anonymous caller, or a device that hasn't minted an account yet):
 *  optional auth, never required, cf. backend/src/middleware/optionalUserAuth.ts. */
function optionalAuth(token?: string | null): HeadersInit | undefined {
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}

/**
 * Le catalogue, et LE CLIENT DÉCLARE CE QU'IL SAIT DESSINER.
 *
 * Le serveur ne peut pas servir à tout le monde un puzzle tiré d'un alphabet que tout le monde
 * ne connaît pas : une version déjà installée n'a pas le dessinateur, et une grille dont les
 * codes dépassent ceux de son repli n'est pas jouable — les cases au-delà n'ont pas de pastille,
 * donc pas de moyen d'être peintes. Une application publiée ne peut pas non plus apprendre à
 * filtrer après coup. Elle peut en revanche être RECONNUE, justement parce qu'elle n'envoie
 * rien : côté serveur, l'absence de ce paramètre vaut « ancien client, sers-lui les alphabets
 * historiques » (cf. backend/src/routes/puzzles.ts).
 *
 * La liste des ids plutôt qu'un numéro de version, et ce n'est pas un détail : un
 * `min_version` demanderait au serveur — ou à l'admin, puzzle par puzzle — de SAVOIR quelle
 * version sait dessiner quoi, correspondance qu'on finit toujours par oublier de mettre à jour.
 * Ici l'exigence est dérivée du registre qui fait le dessin : ajouter un alphabet déclare sa
 * capacité tout seul.
 *
 * `token` optionnel, dans le même esprit : sans compte (ou sans le passer), le catalogue reste
 * strictement celui d'aujourd'hui. Avec, les puzzles des packs que ce compte a débloqués
 * s'ajoutent — cf. unlockPack() plus bas.
 */
export async function fetchPuzzles(token?: string | null): Promise<PuzzleSummary[]> {
  const res = await fetch(
    // `mine=1` : les créations du joueur dans la même réponse (cf. GET /puzzles côté serveur) —
    // seulement avec un compte, sans lequel il n'y en a pas.
    // `lang` : le nom de chaque pack, traduit (cf. PuzzleSummary.packName). Une langue changée
    // recharge la page (setLang), donc le catalogue suivant arrive déjà dans la bonne.
    `${API_URL}/puzzles?alphabets=${knownAlphabetIds().join(',')}&categories=${knownCategories()}&lang=${currentLang()}${token ? '&mine=1' : ''}`,
    { headers: optionalAuth(token) },
  );
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json();
}

/**
 * Un échec HTTP qui dit LEQUEL. Sans le code, l'appelant ne peut pas distinguer « cette feuille
 * n'existe plus » d'un réseau qui a hoqueté — et il finit par afficher le premier message pour
 * les deux, ce qui accuse le puzzle d'un défaut de la ligne (cf. selectPuzzle dans home.ts).
 */
export class HttpError extends Error {
  constructor(readonly status: number) {
    super(`Error ${status}`);
  }
}

/** Même jeton optionnel que fetchPuzzles() ci-dessus, et pour la même raison : une carte du
 *  catalogue déjà filtrée par fetchPuzzles() peut appartenir à un pack débloqué, et cette route
 *  applique le même filtre indépendamment — sans le jeton, ce puzzle-là 404 alors même que la
 *  carte qui a mené ici s'affichait. */
export async function fetchPuzzle(id: string, token?: string | null): Promise<PuzzleDetail> {
  const res = await fetch(`${API_URL}/puzzles/${id}`, { headers: optionalAuth(token) });
  if (!res.ok) throw new HttpError(res.status);
  return res.json();
}

// ---------- le puzzle du jour ----------

/** Une case du calendrier telle que le serveur la connaît : un jour, et la feuille qui lui a été
 *  affectée. Les dimensions et l'alphabet ne sont pas décoratifs — ils servent à reconstruire la
 *  vignette d'un jour peint sur un autre appareil, et à savoir si CETTE version sait dessiner cet
 *  alphabet (cf. backend/src/routes/daily.ts). */
export interface DailyCell {
  day: string;
  id: string;
  alphabetId: string;
  gridW: number;
  gridH: number;
}

export interface DailyMonth {
  /** Le premier jour que le mode connaisse : avant lui, le serveur refuse d'affecter, donc la
   *  flèche « mois précédent » s'éteint. Rendu par le serveur plutôt que recopié ici — une
   *  seconde copie de cette date finirait par ne plus dire la même chose que la première. */
  epoch: string;
  /** Le jour courant EN UTC, tel que le SERVEUR le voit. C'est lui qui décide, pas l'horloge du
   *  téléphone : sans ça, un appareil réglé sur demain afficherait des cases ouvrables que le
   *  serveur refuse, et un appareil en retard cacherait la feuille du jour. */
  today: string;
  /** Les jours DÉJÀ affectés de ce mois, et eux seuls. Un jour absent n'est pas un jour sans
   *  feuille : c'est un jour que personne n'a encore ouvert. */
  days: DailyCell[];
}

/** Le calendrier d'un mois ('YYYY-MM'). Ne consomme rien du stock : regarder n'affecte pas. */
export async function fetchDailyMonth(month: string): Promise<DailyMonth> {
  const res = await fetch(`${API_URL}/daily?month=${month}`);
  if (!res.ok) throw new HttpError(res.status);
  return res.json();
}

/**
 * « Ouvre-moi ce jour-là » — et si personne ne l'a encore fait, le serveur tire sa feuille au
 * hasard dans le stock et la GRAVE pour tout le monde.
 *
 * Un POST parce que le premier appel écrit, et sans jeton parce qu'il n'y a rien à
 * personnaliser : le puzzle du jour est le même pour la planète entière.
 *
 * Deux échecs à distinguer chez l'appelant, d'où le `HttpError` : **409** est le stock épuisé —
 * l'atelier n'a plus rien en avance, ce n'est pas la faute du réseau ni celle du joueur — et
 * **400** un jour que le serveur refuse (dans le futur, ou avant `epoch`), ce que l'écran ne
 * devrait jamais demander puisqu'il n'offre pas ces cases.
 */
export async function claimDaily(day: string): Promise<DailyCell> {
  const res = await fetch(`${API_URL}/daily/${day}`, { method: 'POST' });
  if (!res.ok) throw new HttpError(res.status);
  return res.json();
}

// ---------- anonymous account and cloud saves ----------

/** The token this device holds is no longer known to the server. Its own class because the
 *  caller has to react (drop the token and mint another), not just retry. */
export class Unauthorized extends Error {
  constructor() {
    super('Unauthorized');
  }
}

export interface RemoteProgressSummary {
  puzzleId: string;
  fp: string;
  pct: number;
  done: boolean;
  updatedAt: string;
}

export interface RemoteProgress extends RemoteProgressSummary {
  taps: string; // base64, the very encoding src/progress.ts stores locally
  // null = never rated. Absent from RemoteProgressSummary/the bulk index on purpose: the
  // catalogue only needs a percentage per card, a rating is only read when a puzzle is actually
  // open (cf. src/progress.ts, adoptRemote()).
  rating: number | null;
}

function auth(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

async function guard(res: Response): Promise<Response> {
  if (res.status === 401) throw new Unauthorized();
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res;
}

export interface NewUser {
  id: string;
  username: string;
}

/** Creates an anonymous account: an id (which doubles as the bearer token) and the default
 *  "guestN" pseudo the server picked for it. */
export async function createUser(): Promise<NewUser> {
  const res = await fetch(`${API_URL}/users`, { method: 'POST' });
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json();
}

/** The account's current pseudo — used to re-populate a device that only ever cached the id (a
 *  second install, or a cleared cache). The server also sends `rankOptIn`, left from the league
 *  (retired 2026-10-10, cf. backend/src/utils/community.ts) and no longer read here. */
export async function fetchMe(token: string): Promise<{ username: string }> {
  const res = await guard(await fetch(`${API_URL}/me`, { headers: auth(token) }));
  return res.json();
}

export type BadgeTier = 'bronze' | 'argent' | 'or' | 'platine' | 'prisme';

/** Un badge tel que le serveur le décrit — seuils compris : les règles vivent dans
 *  backend/src/utils/badges.ts et nulle part ici, pour qu'un seuil se règle sans nouvel APK. */
export interface BadgeState {
  /** `library`, `daily`, `share`, `family:dots`…, `category:animaux`… — stable, jamais traduit. */
  id: string;
  group: 'milestone' | 'family' | 'category';
  /** Le compteur RÉEL : il peut redescendre (une feuille recommencée), `tier` non. */
  count: number;
  thresholds: number[];
  /** Le meilleur palier jamais atteint, ou null. */
  tier: BadgeTier | null;
  /** Pour une famille seulement : les alphabets qu'elle regroupe, nommés dans le détail. */
  alphabets?: string[];
}

/** Combien de joueurs tiennent chaque palier — cf. tallyRarity dans backend/src/utils/community.ts.
 *  `held[badgeId][i]` = les joueurs qui ont AU MOINS le palier de rang i. */
export interface Rarity {
  players: number;
  held: Record<string, number[]>;
}

export interface BadgesReport {
  /** L'arrivée du compte, en ISO UTC — null seulement si la ligne a disparu entre-temps. */
  since: string | null;
  badges: BadgeState[];
  /** Absente d'une réponse en cache d'avant ce champ, null quand le serveur n'a pas pu la
   *  calculer : dans les deux cas les badges s'affichent sans pourcentage. */
  rarity?: Rarity | null;
  /** Les chiffres de l'onglet « Moi » (cf. GET /me/badges). Absent d'une réponse en cache d'avant
   *  ce champ, ou d'un serveur plus ancien : la section ne s'affiche alors pas. */
  stats?: MeStats;
}

export interface MeStats {
  /** Toutes les cases peintes par ce compte, feuilles en cours comprises. */
  cells: number;
  /** La feuille finie la plus grande, au nombre de cases peintes ; null avant la première. */
  biggest: { puzzleId: string; title: string; alphabetId: string; cells: number } | null;
  /** La famille d'alphabets (`dots`, `shapes`…) et la catégorie les plus finies. */
  favorites: { family: string | null; category: string | null };
  /** Les instants de fin (ISO UTC) des 92 derniers jours — le jeu les range par jour LOCAL. */
  finishedAt: string[];
}

export async function fetchBadges(token: string): Promise<BadgesReport> {
  // Les alphabets que ce build sait dessiner : le serveur cache les familles de badge dont on ne
  // connaît aucun alphabet (cf. visibleBadges dans backend/src/utils/badges.ts).
  // Et les catégories qu'il sait nommer : celles qu'il ne connaît pas n'ont ni libellé ni médaille.
  const res = await fetch(
    `${API_URL}/me/badges?alphabets=${knownAlphabetIds().join(',')}&categories=${knownCategories()}`,
    { headers: auth(token) },
  );
  return (await guard(res)).json();
}

/** L'onglet « Ensemble » : la semaine de tous les joueurs, sans aucun nom — cf.
 *  backend/src/routes/community.ts. Tous les compteurs portent sur la semaine en cours (lundi
 *  00:00 UTC), sauf `dailyOthers`, qui compte depuis toujours les AUTRES joueurs qui ont fini la
 *  feuille du jour — le jeu ajoute le joueur lui-même s'il l'a finie. */
export interface CommunityReport {
  weekStart: string;
  /** Le jour UTC du serveur — celui du puzzle du jour, et la colonne « aujourd'hui ». */
  day: string;
  sheets: number;
  players: number;
  /** Sept compteurs, lundi d'abord. */
  perDay: number[];
  /** Vingt-quatre compteurs, minuit UTC d'abord — le jeu décale selon le fuseau. Les champs qui
   *  suivent sont optionnels : une réponse gardée en cache d'avant eux ne les porte pas. */
  perHour?: number[];
  /** La semaine dernière jusqu'au même moment (lundi 00:00 → maintenant moins sept jours). */
  lastWeekSoFar?: number;
  newPlayers?: number;
  newPlayersLastWeekSoFar?: number;
  /** Les paliers franchis depuis lundi, par rang ; null quand la rareté n'est pas de la semaine. */
  crossed?: number[] | null;
  dailyPuzzleId: string | null;
  dailyOthers: number | null;
  /** Les feuilles de CE joueur cette semaine. */
  mine: number;
  /** Les cases peintes depuis lundi, tous joueurs confondus. Optionnel : un instantané calculé
   *  avant ce champ ne le porte pas. */
  cells?: number;
  /** La feuille du catalogue la mieux notée cette semaine (trois notes au moins), ou null. */
  topRated?: {
    puzzleId: string;
    title: string;
    alphabetId: string;
    /** Absent d'un instantané d'avant ce champ. */
    packId?: string | null;
    avg: number;
    votes: number;
  } | null;
}

export async function fetchCommunity(token: string): Promise<CommunityReport> {
  const res = await fetch(`${API_URL}/me/community`, { headers: auth(token) });
  return (await guard(res)).json();
}

/** Combien d'AUTRES joueurs ont fini cette feuille — le jeu ajoute le joueur lui-même (cf.
 *  getFinishers côté serveur : sa propre sauvegarde est peut-être encore en route). */
export async function fetchFinishers(token: string, puzzleId: string): Promise<number> {
  const res = await fetch(`${API_URL}/me/finishers/${encodeURIComponent(puzzleId)}`, {
    headers: auth(token),
  });
  return ((await (await guard(res)).json()) as { others: number }).others;
}

/**
 * Déclare un partage. `'later'` quand le serveur ne connaît pas encore la feuille comme
 * terminée (409) : la sauvegarde qui la marque finie peut très bien être encore en route — le
 * partage se propose sur la carte de fin, à l'instant même où elle part. L'appelant garde alors
 * le partage de côté et le renvoie au prochain passage.
 */
export async function postShare(token: string, puzzleId: string): Promise<'ok' | 'later'> {
  const res = await fetch(`${API_URL}/me/shares/${encodeURIComponent(puzzleId)}`, {
    method: 'POST',
    headers: auth(token),
  });
  if (res.status === 409) return 'later';
  await guard(res);
  return 'ok';
}

/** Un partage d'une création du joueur, pour le compteur du jour de l'atelier (POST
 *  /me/creations/:id/share). Le serveur répond 204 quoi qu'il arrive ; seule une panne lève. */
export async function postCreationShare(token: string, id: string): Promise<void> {
  const res = await fetch(`${API_URL}/me/creations/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    headers: auth(token),
  });
  await guard(res);
}

export interface PackSummary {
  id: string;
  title: string;
  /** Facultative, et souvent absente : un pack peut n'être d'aucune catégorie (un mélange), et
   *  ceux d'avant cette colonne n'en ont aucune. Même vocabulaire de slugs que les puzzles —
   *  cf. src/category.ts, qui étiquette les deux. */
  category: string | null;
  /** Les arguments choisis en administration, en JSON (un tableau de slugs) — ou null. Le jeu les
   *  lit, en déduit trois autres de la composition du pack, et n'en affiche que trois : cf.
   *  src/packArgument.ts. */
  arguments: string | null;
  puzzleCount: number;
  /** Composition par alphabet — pour la card du pack (src/packs.ts), qui montre une
   *  illustration par technique présente plutôt qu'un simple total. */
  alphabets: { alphabetId: string; count: number }[];
  // ---- la vitrine (2026-10-23) : tout est facultatif, un Worker d'avant ne les envoie pas ----
  /** Le nom du thème, déjà traduit dans la langue envoyée (cf. fetchAvailablePacks) ; null : pas
   *  de thème, la vitrine montre `title`. */
  themeTitle?: string | null;
  /** Le rang du pack parmi ceux du même thème, quand il y en a plusieurs (« n°2 »). */
  volume?: number | null;
  /** Épinglé dans la bannière par l'atelier, aujourd'hui compris dans ses dates. */
  featured?: { tag: string; order: number | null } | null;
  createdAt?: string;
  /** La première publication du pack, nulle si le serveur ne la connaît pas (avant 2026-10-23) :
   *  le jeu retombe alors sur createdAt (cf. publishedOf dans packs.ts). */
  publishedAt?: string | null;
  /** Chaque puzzle, sans son titre ni son image : de quoi annoncer sa technique, sa difficulté
   *  (shared/difficulty.ts) et sa durée (shared/effort.ts) dans la fiche du pack. */
  puzzles?: { alphabetId: string; gridW: number; gridH: number; zones: number | null }[];
  /** La note moyenne de ses puzzles, leur nombre de notes, et combien de joueurs l'ont débloqué —
   *  un instantané de vingt-quatre heures côté serveur (PACK_STATS_TTL_MS, routes/packs.ts). */
  rating?: number | null;
  votes?: number;
  unlocks?: number;
}

/**
 * Les catégories que ce build sait NOMMER, pour le serveur : il ne nous envoie que les feuilles
 * et les packs de celles-là (cf. backend/src/utils/categories.ts). Même principe que les
 * alphabets juste au-dessus : la capacité se déduit de la table qui fait l'affichage
 * (CATEGORY_LABELS), donc ajouter une catégorie au jeu la déclare toute seule — et une
 * catégorie que le jeu ne connaît pas encore ne lui arrive jamais sous son slug brut.
 */
function knownCategories(): string {
  return Object.keys(CATEGORY_LABELS).join(',');
}

/** The packs this account (or, without a token, anyone) hasn't unlocked yet — same optional
 *  auth as fetchPuzzles(): a device with no account yet still sees the full list, since it has
 *  nothing unlocked. */
export async function fetchAvailablePacks(token?: string | null): Promise<PackSummary[]> {
  const res = await fetch(
    // `lang` : le serveur renvoie le nom du thème de chaque pack déjà traduit (2026-10-23).
    `${API_URL}/packs/available?alphabets=${knownAlphabetIds().join(',')}&categories=${knownCategories()}&lang=${currentLang()}`,
    { headers: optionalAuth(token) },
  );
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json();
}

/** Unlocks a pack for this account — idempotent, safe to call again. Returns the ids of the
 *  puzzles it holds, so the caller can mark them freshly-unlocked without a second round trip
 *  (cf. src/packs.ts). */
export async function unlockPack(
  token: string,
  packId: string,
): Promise<{ packId: string; title: string; puzzleIds: string[] }> {
  const res = await guard(
    await fetch(`${API_URL}/me/packs/${packId}/unlock`, { method: 'POST', headers: auth(token) }),
  );
  return res.json();
}

/**
 * Changes the pseudo. A 409 (already taken) is a normal, expected answer to show inline — not a
 * failure — so it's read here rather than left to `guard()`'s throw-on-non-ok.
 */
export async function updateUsername(
  token: string,
  username: string,
): Promise<{ ok: true; username: string } | { ok: false; reason: 'taken' | 'rejected' }> {
  const res = await fetch(`${API_URL}/me`, {
    method: 'PATCH',
    headers: { ...auth(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  });
  if (res.status === 409) return { ok: false, reason: 'taken' };
  // 422 : bien formé, mais refusé par la modération du serveur (backend/src/utils/blocklist.ts).
  // La liste ne descend PAS jusqu'ici — la publier reviendrait à donner la carte des contournements
  // à qui l'ouvre, et le contrôle qui compte est de toute façon celui du serveur.
  if (res.status === 422) return { ok: false, reason: 'rejected' };
  const { username: saved } = (await (await guard(res)).json()) as { username: string };
  return { ok: true, username: saved };
}

/**
 * Le code de reprise de ce compte — cinq mots, créés par le serveur au premier appel et stables
 * ensuite (cf. `GET /me/recovery`). C'est ce qu'un joueur note pour retrouver sa progression après
 * une désinstallation ou sur un autre téléphone.
 */
export async function fetchRecoveryCode(token: string): Promise<string> {
  const res = await guard(await fetch(`${API_URL}/me/recovery`, { headers: auth(token) }));
  const { code } = (await res.json()) as { code: string };
  return code;
}

/**
 * L'inverse : un code contre le compte qu'il désigne. La SEULE route publique qui rende un jeton,
 * donc appelée sans `Authorization` — l'appareil qui la demande n'en a par définition pas encore.
 *
 * `null` pour un code inconnu (404) : c'est une réponse normale à afficher dans le dialogue, pas
 * une panne, exactement comme le 409 d'un pseudo déjà pris. Un code mal formé reçoit le même 404
 * côté serveur, et c'est voulu — pour le joueur, les deux sont « ce code ne marche pas ».
 */
export async function redeemRecoveryCode(code: string): Promise<NewUser | null> {
  const res = await fetch(`${API_URL}/recovery`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (res.status === 404) return null;
  return (await guard(res)).json();
}

/** Every save of this account, without the tap lists — enough for the catalogue's percentages. */
export async function fetchProgressIndex(token: string): Promise<RemoteProgressSummary[]> {
  const res = await guard(await fetch(`${API_URL}/me/progress`, { headers: auth(token) }));
  return res.json();
}

/** One save, tap list included. null when the account has nothing for this puzzle. */
export async function fetchRemoteProgress(
  token: string,
  puzzleId: string,
): Promise<RemoteProgress | null> {
  const res = await fetch(`${API_URL}/me/progress/${puzzleId}`, { headers: auth(token) });
  if (res.status === 404) return null;
  return (await guard(res)).json();
}

/** Starting over: the save is dropped, not merged down. Cf. the route's comment — every other
 *  write grows, this one is the exception and needs its own verb. */
export async function deleteRemoteProgress(token: string, puzzleId: string): Promise<void> {
  const res = await fetch(`${API_URL}/me/progress/${puzzleId}`, {
    method: 'DELETE',
    headers: auth(token),
  });
  if (res.status === 404) return;
  await guard(res);
}

// ---------- les créations : les feuilles que le joueur fabrique depuis ses photos ----------
// SUR LE TÉLÉPHONE SEULEMENT depuis le 2026-10-30 (cf. src/creationStore.ts) : ni la photo ni la
// grille ne partent plus. Restent ici le préfixe qui les reconnaît, le compteur anonyme du tableau
// de bord, et ce que le rapatriement des anciennes demande au serveur (fetchPuzzle, /me/progress,
// deleteCreation), le temps qu'il les purge.

/** Le préfixe des identifiants de création — le même que backend/src/utils/creations.ts, dupliqué
 *  pour la raison habituelle (le Worker n'importe rien de `src/`). Lu par le jeu pour retirer ce
 *  qui n'a pas de sens sur une feuille que personne d'autre ne voit : le partage, « avec toi, N
 *  joueurs ». */
export const CREATION_PREFIX = 'c_';

export function isCreation(id: string | null | undefined): boolean {
  return !!id && id.startsWith(CREATION_PREFIX);
}

/**
 * Une création refusée par un plafond : `account`, le joueur en a trop et en supprime une (levée
 * par Créer lui-même, cf. CREATIONS_PER_ACCOUNT dans src/creer.ts) ; `full`, le service n'en prend
 * plus pour personne — c'était le plafond commun du serveur, qui ne reçoit plus de créations
 * depuis le 2026-10-30 : plus rien ne la lève, le message reste pour le jour où on y reviendrait.
 */
export class TooManyCreations extends Error {
  constructor(
    readonly reason: 'account' | 'full',
    readonly max: number,
  ) {
    super('Too many creations');
  }
}

/**
 * Une création de plus, pour le compteur du tableau de bord (« Puzzles créés ») — et RIEN de la
 * création : ni grille, ni titre, ni technique. Depuis le 2026-10-30 elle reste sur le téléphone
 * (src/creationStore.ts) ; POST /me/creations, qui la gardait, refuse désormais tout envoi (les
 * anciennes versions y lisent « momentanément indisponible »). Aucune réponse attendue, aucune
 * erreur remontée : ce n'est qu'une statistique.
 */
export async function noteCreationMade(token: string): Promise<void> {
  await fetch(`${API_URL}/me/creations/made`, { method: 'POST', headers: auth(token) }).catch(
    () => undefined,
  );
}

/**
 * Une étape de l'initiation, pour l'entonnoir du tableau de bord (POST /intro/:step, cf.
 * routes/intro.ts) — sans jeton ni rien qui désigne le joueur : la première étape arrive avant
 * qu'aucun compte n'existe. Rend vrai quand l'envoi est réglé — compté, ou refusé pour de bon
 * (400, une étape que ce serveur ne connaît pas) —, faux quand il est à refaire (hors ligne).
 */
export async function noteIntroStep(step: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/intro/${encodeURIComponent(step)}`, { method: 'POST' });
    return res.ok || res.status === 400;
  } catch {
    return false;
  }
}

/** 404 toléré comme pour deleteRemoteProgress : une création déjà partie est le résultat voulu. */
export async function deleteCreation(token: string, id: string): Promise<void> {
  const res = await fetch(`${API_URL}/me/creations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: auth(token),
  });
  if (res.status === 404) return;
  await guard(res);
}

/**
 * Deletes the account and every save attached to it, server side. Deliberately NOT tolerant of a
 * 404 the way deleteRemoteProgress is: this is the one call whose failure must be visible. Wiping
 * the device while the server kept the row would leave data nobody can ever ask to have deleted —
 * the token is the only handle on it.
 */
export async function deleteAccount(token: string): Promise<void> {
  const res = await fetch(`${API_URL}/me`, { method: 'DELETE', headers: auth(token) });
  // 401 means the row is already gone — the deletion this call was asking for has happened.
  if (res.status === 401) return;
  await guard(res);
}

/** Enregistre (ou ré-enregistre) le token FCM de cet appareil, cf. src/push.ts. */
export async function registerPushToken(token: string, pushToken: string): Promise<void> {
  await guard(
    await fetch(`${API_URL}/me/push-token`, {
      method: 'PUT',
      headers: { ...auth(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: pushToken }),
    }),
  );
}

/** Désactivation côté joueur — 404 toléré, un token déjà purgé (ou jamais enregistré) n'est pas
 *  une erreur ici, contrairement à deleteAccount(). */
export async function unregisterPushToken(token: string, pushToken: string): Promise<void> {
  const res = await fetch(`${API_URL}/me/push-token`, {
    method: 'DELETE',
    headers: { ...auth(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: pushToken }),
  });
  if (res.status === 404) return;
  await guard(res);
}

/** Pushes the COMPLETE local list (never a delta) and returns the server's merge of it with
 *  whatever other devices had already sent. */
export async function pushRemoteProgress(
  token: string,
  puzzleId: string,
  body: { fp: string; taps: string; pct: number; done: boolean; rating?: number },
): Promise<RemoteProgress> {
  const res = await fetch(`${API_URL}/me/progress/${puzzleId}`, {
    method: 'PUT',
    headers: { ...auth(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  // Une CRÉATION refusée en 404 n'existe plus pour ce compte — supprimée depuis un autre appareil,
  // le plus souvent. Le dire à part : l'appelant efface alors la sauvegarde locale, qui sinon
  // repartait à chaque lancement (une requête et deux lignes lues pour rien, indéfiniment — revue
  // du 2026-10-16). Pour une feuille du catalogue, rien ne change : le serveur n'y répond jamais 404.
  if (res.status === 404 && isCreation(puzzleId)) throw new HttpError(404);
  return (await guard(res)).json();
}

/**
 * Un plantage de l'appareil, envoyé au serveur qui le journalise (cf.
 * backend/src/routes/clientErrors.ts). ANONYME et sans jeton : rien ici ne doit pouvoir désigner
 * un joueur, et l'envoyer authentifié serait la première chose qu'on regretterait.
 *
 * `keepalive` : l'erreur qui compte le plus est celle qui emmène la page avec elle, et une
 * requête ordinaire meurt avec le contexte qui l'a lancée. Aucune promesse rendue, aucun `await`
 * possible chez l'appelant, et toute panne d'envoi est avalée — un rapport perdu n'est rien, un
 * rapport qui relance une erreur pendant qu'on traite une erreur est une boucle.
 */
export function reportClientError(body: Record<string, unknown>): void {
  try {
    void fetch(`${API_URL}/client-errors`, {
      method: 'POST',
      // `text/plain` pour un corps qui EST du JSON, et ce n'est pas une négligence : c'est le
      // seul type de contenu qui fasse de cette requête une requête « simple » au sens CORS,
      // donc SANS pré-vol. Avec `application/json`, le navigateur envoie d'abord un OPTIONS et
      // n'envoie le rapport que si la réponse lui convient — un aller-retour de plus pendant
      // que la page meurt, et un rapport perdu le jour où l'origine n'est pas dans la liste
      // (mesuré : le POST ne partait pas du tout). Le serveur lit le corps avec `req.json()`,
      // qui parse ce qu'on lui donne sans regarder l'en-tête.
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* rien : on est déjà en train de traiter une erreur */
  }
}
